<template>
  <div class="ledger-page ledger-asset-print pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-start flex-wrap mb-1">
        <div>
          <h4 class="mb-50">
            {{ $t('ledger_assets_statement') }}
          </h4>
          <div
            v-if="statement"
            class="text-muted"
          >
            {{ statement.as_of }}
          </div>
        </div>
        <div class="d-flex flex-wrap">
          <b-form-input
            v-model="asOf"
            type="date"
            class="mr-50 mb-50 ledger-asset-date"
          />
          <b-button
            variant="primary"
            class="mr-50 mb-50 rounded-pill"
            :disabled="loading"
            @click="load"
          >
            {{ $t('ledger_assets_load') }}
          </b-button>
          <b-button
            variant="outline-secondary"
            class="mb-50 rounded-pill d-none d-md-inline-block"
            @click="print"
          >
            {{ $t('ledger_assets_print') }}
          </b-button>
        </div>
      </div>

      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner variant="primary" />
      </div>
      <div
        v-else-if="statement"
      >
        <section
          v-for="group in groups"
          :key="groupKey(group)"
          class="mb-2"
        >
          <h5 class="mb-50">
            {{ enumLabel(group.category, $i18n.locale) }}
          </h5>
          <div class="d-none d-md-block">
            <b-table
              :items="assetsFor(group)"
              :fields="fields"
              small
              responsive
              class="ledger-dense-table"
            >
              <template #cell(name)="data">
                <b-link :to="{ name: 'ledger-assets-view', params: { id: data.item.id } }">
                  {{ displayName(data.item) }}
                </b-link>
                <div class="text-muted small">
                  {{ data.item.asset_code }}
                </div>
              </template>
              <template #cell(cost)="data">
                <span class="ledger-num">{{ money(data.item.cost) }}</span>
              </template>
              <template #cell(accumulated_depreciation)="data">
                <span class="ledger-num">{{ money(data.item.accumulated_depreciation) }}</span>
              </template>
              <template #cell(net_book_value)="data">
                <span class="ledger-num">{{ money(data.item.net_book_value) }}</span>
              </template>
            </b-table>
          </div>
          <div class="d-md-none">
            <article
              v-for="asset in assetsFor(group)"
              :key="asset.id"
              class="ledger-asset-card"
            >
              <b-link :to="{ name: 'ledger-assets-view', params: { id: asset.id } }">
                {{ displayName(asset) }}
              </b-link>
              <div class="ledger-num">
                {{ $t('ledger_assets_nbv') }} {{ money(asset.net_book_value) }}
              </div>
            </article>
          </div>
          <div class="ledger-asset-subtotal ledger-num">
            {{ $t('ledger_assets_subtotal') }}
            {{ money(group.cost) }} · {{ money(group.accumulated_depreciation) }} · {{ money(group.net_book_value) }}
          </div>
        </section>

        <div
          v-if="totals"
          class="ledger-asset-total ledger-num"
        >
          {{ $t('ledger_assets_total') }}
          {{ totals.count }} · {{ money(totals.cost) }} · {{ money(totals.accumulated_depreciation) }} · {{ money(totals.net_book_value) }}
        </div>
      </div>
    </b-card>

    <div
      v-if="totals"
      class="ledger-asset-sticky d-md-none"
    >
      <span>{{ $t('ledger_assets_nbv') }}</span>
      <strong class="ledger-num">{{ money(totals.net_book_value) }}</strong>
    </div>
  </div>
</template>

<script>
import {
  BCard, BFormInput, BButton, BTable, BLink, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getFixedAssetStatement } from '@/api/accounting/fixedAssets'
import {
  oneFrom, enumValue, enumLabel, formatLedgerMoney, todayIso, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'FixedAssetStatement',
  components: {
    BCard, BFormInput, BButton, BTable, BLink, BSpinner,
  },
  data() {
    return {
      loading: false,
      asOf: todayIso(),
      statement: null,
    }
  },
  computed: {
    groups() {
      return (this.statement && this.statement.by_category) || []
    },
    totals() {
      return (this.statement && this.statement.totals) || null
    },
    fields() {
      return [
        { key: 'name', label: this.$t('ledger_name') },
        { key: 'acquisition_date', label: this.$t('ledger_assets_acquired') },
        { key: 'cost', label: this.$t('ledger_assets_cost') },
        { key: 'accumulated_depreciation', label: this.$t('ledger_assets_accumulated') },
        { key: 'net_book_value', label: this.$t('ledger_assets_nbv') },
      ]
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    enumLabel,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    displayName(asset) {
      if (this.$i18n.locale === 'en') return asset.name || asset.name_ar || ''
      return asset.name_ar || asset.name || ''
    },
    groupKey(group) {
      return enumValue(group.category) || group.category
    },
    assetsFor(group) {
      const key = enumValue(group.category)
      return ((this.statement && this.statement.assets) || []).filter(asset => enumValue(asset.category) === key)
    },
    print() {
      window.print()
    },
    async load() {
      this.loading = true
      try {
        const res = await getFixedAssetStatement({ as_of: this.asOf })
        this.statement = oneFrom(res)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: apiErrorMessage(error, this.$t('ledger_error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-asset-date {
  max-width: 11rem;
}
.ledger-asset-card {
  border-bottom: 1px solid #eee;
  padding: 0.75rem 0.15rem;
}
.ledger-asset-subtotal,
.ledger-asset-total {
  padding: 0.5rem 0;
  font-weight: 600;
}
.ledger-asset-total {
  border-top: 2px solid #06284d;
}
.ledger-asset-sticky {
  position: fixed;
  bottom: 0;
  inset-inline: 0;
  z-index: 20;
  display: flex;
  justify-content: space-between;
  background: #fff;
  border-top: 1px solid #e6e6e6;
  padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
@media print {
  .ledger-asset-sticky,
  .btn,
  .form-control {
    display: none !important;
  }
}
</style>
